import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
} from 'recharts';
import {
  ArrowRight,
  DollarSign,
  Users,
  Wrench,
} from 'lucide-react';
import {
  applyInfraFilters,
  calculateInfraAverageDurationHours,
  buildFilterLabel,
  distinctRowsBy,
  formatInfraDurationHours,
  formatInfraMonth,
  getInfraHistoryMonth,
  groupCount,
  isInfraClosedInPeriod,
  isInfraOpenedInPeriod,
  normalizeNumber,
  sameFilter,
} from '../utils/infraAnalytics';
import { useInfraestruturaData } from '../context/InfraestruturaDataContext';
import InfraEvolutionNotice from './InfraEvolutionNotice';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const SummaryCard = ({ title, value, hint, accent }) => (
  <div className={`${panelClassName} relative flex h-full min-h-[152px] flex-col overflow-hidden`}>
    <div
      className="pointer-events-none absolute inset-x-4 top-0 h-px"
      style={{ background: accent || 'linear-gradient(90deg, rgba(59,130,246,0.0), rgba(59,130,246,0.65), rgba(59,130,246,0.0))' }}
    />
    <p className="max-w-full text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)] sm:text-[11px]">
      {title}
    </p>
    <p className="mt-3 break-words text-[clamp(1.9rem,2.6vw,3rem)] font-semibold leading-[0.94] tracking-[-0.03em] text-[color:var(--text-primary)]">
      {value}
    </p>
    {hint ? <p className="mt-auto pt-3 text-xs leading-5 text-[color:var(--text-muted)]">{hint}</p> : null}
  </div>
);

const ClickableList = ({ items, field, filters, onToggle, valueFormatter }) => (
  <div className="space-y-2.5">
    {items.map((item) => {
      const normalizedValue = item.label === 'Não informado' ? null : item.label;
      const isActive = Array.isArray(filters)
        && filters.some((filter) => sameFilter(filter, { field, value: normalizedValue }));

      return (
        <button
          key={`${field}:${String(item.label)}`}
          type="button"
          onClick={() => onToggle(field, normalizedValue)}
          className={`flex w-full items-center justify-between gap-3 rounded-[14px] border px-3 py-2.5 text-left text-sm transition-[border-color,background-color,color,transform] duration-200 hover:-translate-y-[1px] ${
            isActive
              ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]/88 text-[color:var(--text-primary)]'
              : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/72'
          }`}
        >
          <span className="min-w-0 truncate">{item.label}</span>
          <span className="shrink-0 font-semibold text-[color:var(--accent)]">{valueFormatter(item)}</span>
        </button>
      );
    })}
  </div>
);

const ShortcutCard = ({ icon: Icon, title, description, buttonLabel, onClick }) => (
  <div className={`${panelClassName} flex h-full flex-col justify-between`}>
    <div>
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 text-[color:var(--accent)]">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-[color:var(--text-primary)]">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-[color:var(--text-muted)]">{description}</p>
    </div>

    <button
      type="button"
      onClick={onClick}
      className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/55 hover:text-[color:var(--accent)]"
    >
      {buttonLabel}
      <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

const buildEvolutionFallback = (rows) => {
  const map = new Map();

  distinctRowsBy(rows, 'id_os').forEach((row) => {
    const month = getInfraHistoryMonth(row, 'ano_mes_fechamento');
    if (isInfraClosedInPeriod(row) && month !== 'Não informado') {
      const bucket = map.get(month) || { month: formatInfraMonth(month), rawMonth: month, total_os: 0, total_valor: 0 };
      bucket.total_os += 1;
      bucket.total_valor += normalizeNumber(row?.total_valor);
      map.set(month, bucket);
    }
  });

  return Array.from(map.values()).sort((a, b) => a.rawMonth.localeCompare(b.rawMonth, 'pt-BR'));
};

const HistoricalDot = ({ cx, cy, payload, stroke, activeRangeMonth }) => {
  if (cx == null || cy == null) {
    return null;
  }

  const isActive = payload?.rawMonth === activeRangeMonth;

  if (!isActive) {
    return <circle cx={cx} cy={cy} r={3} fill={stroke} stroke="#ffffff" strokeWidth={1.5} />;
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={10} fill="rgba(96,165,250,0.18)" />
      <circle cx={cx} cy={cy} r={5} fill={stroke} stroke="#dbeafe" strokeWidth={2} />
    </g>
  );
};

const InfraOverviewDashboard = ({ onSelectView, isMobile = false }) => {
  const {
    rowsRaw,
    rowsFiltered,
    evolutionMonths,
    filters,
    toggleFilter,
    selectHistoricalMonth,
    activeRangeMonth,
  } = useInfraestruturaData();

  const uniqueRows = useMemo(() => distinctRowsBy(rowsFiltered, 'id_os'), [rowsFiltered]);
  const closedRows = useMemo(() => uniqueRows.filter((row) => isInfraClosedInPeriod(row)), [uniqueRows]);
  const openedRows = useMemo(() => uniqueRows.filter((row) => isInfraOpenedInPeriod(row)), [uniqueRows]);
  const rowsWithoutCollaboratorFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'funcionario')),
    [filters, rowsRaw],
  );
  const rowsWithoutSubjectFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'assunto')),
    [filters, rowsRaw],
  );
  const rowsWithoutCityFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'cidade_nome')),
    [filters, rowsRaw],
  );
  const rowsWithoutReferenceFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'estrutura_referencia_normalizada')),
    [filters, rowsRaw],
  );
  const closedRowsWithoutCollaboratorFilter = useMemo(
    () => distinctRowsBy(rowsWithoutCollaboratorFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutCollaboratorFilter],
  );
  const closedRowsWithoutSubjectFilter = useMemo(
    () => distinctRowsBy(rowsWithoutSubjectFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutSubjectFilter],
  );
  const closedRowsWithoutCityFilter = useMemo(
    () => distinctRowsBy(rowsWithoutCityFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutCityFilter],
  );
  const closedRowsWithoutReferenceFilter = useMemo(
    () => distinctRowsBy(rowsWithoutReferenceFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutReferenceFilter],
  );

  const kpis = useMemo(() => {
    const totalAbertas = openedRows.length;
    const totalFechadas = closedRows.length;
    const totalValor = closedRows.reduce((acc, row) => acc + normalizeNumber(row?.total_valor), 0);
    const mediaAberturaExecucao = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_hora_execucao');
    const mediaExecucaoFechamento = calculateInfraAverageDurationHours(closedRows, 'data_hora_execucao', 'data_fechamento');
    const mediaAberturaFechamento = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_fechamento');
    const rompimentos = closedRows.reduce((acc, row) => acc + (normalizeNumber(row?.is_rompimento) ? 1 : 0), 0);
    const taxaRompimento = totalFechadas > 0 ? (rompimentos / totalFechadas) * 100 : 0;

    return {
      totalAbertas,
      totalFechadas,
      totalValor,
      mediaAberturaExecucao,
      mediaExecucaoFechamento,
      mediaAberturaFechamento,
      taxaRompimento,
      filteredCount: uniqueRows.length,
      rawCount: distinctRowsBy(rowsRaw, 'id_os').length,
    };
  }, [closedRows, openedRows, rowsRaw, uniqueRows]);

  const evolutionData = useMemo(() => {
    if (Array.isArray(evolutionMonths) && evolutionMonths.length > 0) {
      return evolutionMonths.map((item) => ({
        month: formatInfraMonth(item?.ano_mes || 'Não informado'),
        rawMonth: item?.ano_mes || 'Não informado',
        total_os: Number(item?.total_os ?? item?.finalizadas ?? 0),
        total_valor: Number(item?.total_valor ?? 0),
      }));
    }

    return buildEvolutionFallback(rowsFiltered);
  }, [evolutionMonths, rowsFiltered]);

  const topCollaborators = useMemo(
    () => groupCount(closedRowsWithoutCollaboratorFilter, 'funcionario', { distinctBy: 'id_os' }).slice(0, 6),
    [closedRowsWithoutCollaboratorFilter],
  );

  const topSubjects = useMemo(
    () => groupCount(closedRowsWithoutSubjectFilter, 'assunto', { distinctBy: 'id_os' }).slice(0, 6),
    [closedRowsWithoutSubjectFilter],
  );

  const topCities = useMemo(
    () => groupCount(closedRowsWithoutCityFilter, 'cidade_nome', { distinctBy: 'id_os' }).slice(0, 6),
    [closedRowsWithoutCityFilter],
  );

  const topReferences = useMemo(
    () => groupCount(closedRowsWithoutReferenceFilter, 'estrutura_referencia_normalizada', { distinctBy: 'id_os' }).slice(0, 6),
    [closedRowsWithoutReferenceFilter],
  );

  const handleHistoricalMonthSelect = (payload) => {
    const candidate = payload?.activePayload?.[0]?.payload
      || payload?.activePayload?.[1]?.payload
      || payload?.payload
      || payload;

    const rawMonth = candidate?.rawMonth || candidate?.month;

    if (rawMonth && rawMonth !== 'Não informado') {
      selectHistoricalMonth(rawMonth);
    }
  };

  const handleToggle = (field, value) => {
    toggleFilter({
      field,
      value,
      label: buildFilterLabel(field, value),
    });
  };

  const chartTickProps = isMobile ? { fontSize: 10 } : { fontSize: 11 };
  const chartMargin = isMobile
    ? { top: 8, right: 8, left: -14, bottom: 8 }
    : { top: 8, right: 20, left: 0, bottom: 8 };
  const xAxisProps = isMobile
    ? { interval: 'preserveStartEnd', minTickGap: 22, tickMargin: 8 }
    : { interval: 0, minTickGap: 10, tickMargin: 6 };
  const legendWrapperStyle = isMobile ? { paddingTop: 12, fontSize: 11 } : undefined;

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        <SummaryCard
          title="OS abertas"
          value={kpis.totalAbertas.toLocaleString('pt-BR')}
          hint={`${kpis.filteredCount.toLocaleString('pt-BR')} filtradas de ${kpis.rawCount.toLocaleString('pt-BR')} no dataset`}
        />
        <SummaryCard title="OS finalizadas" value={kpis.totalFechadas.toLocaleString('pt-BR')} />
        <SummaryCard title="Custo total" value={currencyFormatter.format(kpis.totalValor)} />
        <SummaryCard title="Abertura até execução" value={formatInfraDurationHours(kpis.mediaAberturaExecucao)} />
        <SummaryCard title="Execução até finalização" value={formatInfraDurationHours(kpis.mediaExecucaoFechamento)} />
        <SummaryCard title="Abertura até finalização" value={formatInfraDurationHours(kpis.mediaAberturaFechamento)} />
        <SummaryCard title="Taxa de rompimento" value={`${kpis.taxaRompimento.toFixed(2)}%`} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className={`${panelClassName} xl:col-span-7`}>
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Tendência histórica</h3>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">A série mensal permanece histórica e o clique atualiza o período principal para o mês selecionado.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {activeRangeMonth ? (
                <span className="rounded-full border border-[color:var(--accent)]/35 bg-[color:var(--accent)]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
                  Mês ativo: {formatInfraMonth(activeRangeMonth)}
                </span>
              ) : null}
              <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                12 meses
              </span>
            </div>
          </div>
          <InfraEvolutionNotice />
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={evolutionData}
                margin={chartMargin}
                onClick={handleHistoricalMonthSelect}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
                {activeRangeMonth ? (
                  <ReferenceArea
                    x1={formatInfraMonth(activeRangeMonth)}
                    x2={formatInfraMonth(activeRangeMonth)}
                    fill="rgba(59,130,246,0.10)"
                    strokeOpacity={0}
                  />
                ) : null}
                <XAxis dataKey="month" tick={chartTickProps} stroke="var(--text-muted)" {...xAxisProps} />
                <YAxis yAxisId="left" tick={chartTickProps} width={isMobile ? 30 : 40} stroke="var(--text-muted)" />
                <YAxis yAxisId="right" orientation="right" tick={chartTickProps} width={isMobile ? 34 : 48} stroke="var(--text-muted)" />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelStyle={{ color: 'var(--text-primary)' }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                  formatter={(value, _name) => (
                    _name === 'Valor' ? currencyFormatter.format(Number(value || 0)) : Number(value || 0).toLocaleString('pt-BR')
                  )}
                />
                <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="total_os"
                  name="OS"
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={(props) => <HistoricalDot {...props} activeRangeMonth={activeRangeMonth} />}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_valor"
                  name="Valor"
                  stroke="#0ea5e9"
                  strokeWidth={2.5}
                  dot={(props) => <HistoricalDot {...props} activeRangeMonth={activeRangeMonth} />}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${panelClassName} xl:col-span-5`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Top colaboradores</h3>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">Entradas mais relevantes no recorte filtrado.</p>
            </div>
            <button
              type="button"
              onClick={() => onSelectView('colaborador')}
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--header-control-border-subtle)] px-3 py-1.5 text-xs font-medium text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)]"
            >
              Abrir aba
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <ClickableList
            items={topCollaborators}
            field="funcionario"
            filters={filters}
            onToggle={handleToggle}
            valueFormatter={(item) => item.count.toLocaleString('pt-BR')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className={`${panelClassName} xl:col-span-4`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Assuntos com mais OS</h3>
            <button
              type="button"
              onClick={() => onSelectView('ordem-servico')}
              className="text-xs font-medium text-[color:var(--accent)] hover:underline"
            >
              Ver análise completa
            </button>
          </div>
          <ClickableList
            items={topSubjects}
            field="assunto"
            filters={filters}
            onToggle={handleToggle}
            valueFormatter={(item) => item.count.toLocaleString('pt-BR')}
          />
        </div>

        <div className={`${panelClassName} xl:col-span-4`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Referências recorrentes</h3>
            <button
              type="button"
              onClick={() => onSelectView('ordem-servico')}
              className="text-xs font-medium text-[color:var(--accent)] hover:underline"
            >
              Ir para OS
            </button>
          </div>
          <ClickableList
            items={topReferences}
            field="estrutura_referencia_normalizada"
            filters={filters}
            onToggle={handleToggle}
            valueFormatter={(item) => item.count.toLocaleString('pt-BR')}
          />
        </div>

        <div className={`${panelClassName} xl:col-span-4`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Distribuição por cidade</h3>
            <span className="text-[11px] text-[color:var(--text-muted)]">Clique para cruzar</span>
          </div>
          <ClickableList
            items={topCities}
            field="cidade_nome"
            filters={filters}
            onToggle={handleToggle}
            valueFormatter={(item) => item.count.toLocaleString('pt-BR')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ShortcutCard
          icon={Users}
          title="Colaborador"
          description="Aprofunde ranking, produtividade, horas médias e totais por colaborador sem perder os filtros já aplicados."
          buttonLabel="Abrir colaborador"
          onClick={() => onSelectView('colaborador')}
        />
        <ShortcutCard
          icon={Wrench}
          title="Ordem de serviço"
          description="Explore finalizações, rompimentos, referências, cidades e a tabela detalhada a partir do mesmo recorte."
          buttonLabel="Abrir OS"
          onClick={() => onSelectView('ordem-servico')}
        />
        <ShortcutCard
          icon={DollarSign}
          title="Valores"
          description="Veja custo total, distribuição financeira por assunto, estrutura, projeto e evolução mensal de valor."
          buttonLabel="Abrir valores"
          onClick={() => onSelectView('valores')}
        />
      </div>
    </div>
  );
};

export default InfraOverviewDashboard;
